import { getMessaging, getToken, onMessage } from "firebase/messaging";
import app from "../utils/firebase/Firebase.utils";
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
} from "firebase/firestore";
import { db } from "../utils/firebase/Firebase.utils";
import { sendNotification } from "../utils/notifications";

const messaging = getMessaging(app);

export const requestNotificationPermission = async () => {
  try {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      const token = await getFCMToken();
      return token;
    }
    return null;
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return null;
  }
};

export const getFCMToken = async () => {
  try {
    const currentToken = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    });
    return currentToken;
  } catch (error) {
    console.error("Error getting FCM token:", error);
    return null;
  }
};

export const onMessageListener = () => {
  return new Promise((resolve) => {
    onMessage(messaging, (payload) => {
      // Display notification using the Notification API for foreground messages
      if (payload.notification) {
        const notificationTitle = payload.notification.title;
        const notificationOptions = {
          body: payload.notification.body,
          icon: "/logo192.png",
          data: payload.data,
        };

        // Create and show the notification
        try {
          const notification = new Notification(
            notificationTitle,
            notificationOptions
          );

          // Handle notification click
          notification.onclick = () => {
            if (payload.data && payload.data.url) {
              window.location.href = payload.data.url;
            }
            notification.close();
          };
        } catch (error) {
          console.error("Error showing notification:", error);
        }
      }
      resolve(payload);
    });
  });
};

export const saveUserToken = async (userId, token) => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      fcmToken: token,
      notificationEnabled: true,
    });
    return true;
  } catch (error) {
    console.error("Error saving user token:", error);
    return false;
  }
};

export const removeUserToken = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      fcmToken: null,
      notificationEnabled: false,
    });
    return true;
  } catch (error) {
    console.error("Error removing user token:", error);
    return false;
  }
};

// Utility functions for different notification types
export const createGuestPassStatusNotification = (
  passId,
  status,
  unitNumber
) => {
  const title = "Guest Pass Status Update";
  const body = `Guest pass for Unit ${unitNumber} is now ${status}`;

  return {
    title,
    body,
    subject: title, // For Firestore
    message: body, // For Firestore
    data: {
      url: `/guests/${passId}`,
      type: "guest_pass_status",
      passId: passId,
      status: status,
    },
  };
};

// Helper for creating status-specific notifications with appropriate styling and messages
export const createStatusSpecificNotification = (
  passId,
  status,
  unitNumber,
  audience = "resident"
) => {
  let title = "";
  let body = "";
  let priority = "normal";
  let url = "";

  // Different message based on audience (resident, staff, security)
  switch (audience) {
    case "security":
      // Security-focused messages
      if (status === "admitted") {
        title = "Guest Admission Alert";
        body = `Guests for Unit ${unitNumber} have been admitted to the property. Please verify their credentials.`;
        priority = "high";
        url = `/guests-card-details/${passId}`;
      } else if (status === "completed") {
        title = "Guest Departure Alert";
        body = `Guests for Unit ${unitNumber} have completed their visit. Please confirm departure.`;
        priority = "high";
      } else if (status === "approved") {
        title = "New Approved Guest Pass";
        body = `Guest pass for Unit ${unitNumber} has been approved. Expect guest arrival soon.`;
      } else {
        title = "Guest Pass Status Change";
        body = `Guest pass for Unit ${unitNumber} is now ${status}.`;
      }
      break;

    case "staff":
      // Staff-focused messages
      if (status === "pending") {
        title = "New Guest Pass Request";
        body = `A new guest pass request has been submitted for Unit ${unitNumber}. Please review.`;
        priority = "high";
      } else {
        title = "Guest Pass Status Change";
        body = `Guest pass for Unit ${unitNumber} is now ${status}.`;
      }
      break;

    case "resident":
    default:
      // Resident-focused messages
      switch (status) {
        case "approved":
          title = "Guest Pass Approved";
          body = `Your guest pass request for Unit ${unitNumber} has been approved.`;
          break;
        case "rejected":
          title = "Guest Pass Rejected";
          body = `Your guest pass request for Unit ${unitNumber} has been rejected.`;
          priority = "high";
          break;
        case "admitted":
          title = "Guests Admitted";
          body = `Your guests have been admitted to the property.`;
          break;
        case "completed":
          title = "Guest Visit Completed";
          body = `Your guest pass for Unit ${unitNumber} has been marked as completed.`;
          break;
        case "returned":
          title = "Guest Pass Returned";
          body = `Your guest pass for Unit ${unitNumber} has been returned.`;
          break;
        default:
          title = "Guest Pass Status Update";
          body = `Your guest pass for Unit ${unitNumber} is now ${status}.`;
      }
  }

  return {
    title,
    body,
    subject: title, // For Firestore
    message: body, // For Firestore
    data: {
      url: url || `/guests/${passId}`,
      type: "guest_pass_status",
      passId,
      status,
      priority,
    },
  };
};

export const createGuestPassAssignmentNotification = (
  passId,
  unitNumber,
  assignedTo = []
) => {
  const assignedNames = assignedTo.map((user) => user.label).join(", ");
  const title = "Guest Pass Assignment";
  const body =
    assignedTo.length > 0
      ? `Guest pass for Unit ${unitNumber} assigned to ${assignedNames}`
      : `Guest pass for Unit ${unitNumber} has been unassigned`;

  return {
    title,
    body,
    subject: title, // For Firestore
    message: body, // For Firestore
    data: {
      url: `/guests/${passId}`,
      type: "guest_pass_assignment",
      passId: passId,
    },
  };
};

export const createGuestPassCommentNotification = (
  passId,
  unitNumber,
  commenter,
  commentText
) => {
  const shortComment =
    commentText.length > 50
      ? `${commentText.substring(0, 50)}...`
      : commentText;

  const title = "New Comment on Guest Pass";
  const body = `${commenter} commented on Unit ${unitNumber} guest pass: ${shortComment}`;

  return {
    title,
    body,
    subject: title, // For Firestore
    message: body, // For Firestore
    data: {
      url: `/guests/${passId}`,
      type: "guest_pass_comment",
      passId: passId,
    },
  };
};

// Function to store a notification in Firestore regardless of FCM success
export const storeNotification = async (userEmail, notification) => {
  try {
    // Skip storing if this is an FCM-specific notification
    if (notification.data && notification.data.isFCM === true) {
      return true;
    }

    // Find user by email
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", userEmail));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return false;
    }

    const userDoc = querySnapshot.docs[0];

    // Prepare notification data - map FCM fields to Firestore fields
    const notificationData = {
      // Map FCM title to Firestore subject
      subject: notification.title || notification.subject || "",
      // Map FCM body to Firestore message
      message: notification.body || notification.message || "",
      // Keep the data field as is
      data: notification.data || {},
      // Use provided from/fromName or set defaults
      from: notification.from || "system",
      fromName: notification.fromName || "System Notification",
      dateTime: new Date(),
      read: false,
      to: userEmail,
      user: userEmail,
      userId: userDoc.id,
      userEmail: userEmail,
      // Mark as Firestore notification
      isFirestore: true,
    };

    // Store notification in Firestore
    const notificationsRef = collection(db, "notifications");
    await addDoc(notificationsRef, notificationData);

    return true;
  } catch (error) {
    console.error("Error storing notification:", error);
    return false;
  }
};

// Create payment status notification for residents
export const createPaymentStatusNotification = (
  paymentId,
  status,
  amount,
  description,
  billingId
) => {
  let title = "";
  let body = "";
  let priority = "normal";

  switch (status) {
    case "verified":
      title = "Payment Verified";
      body = `Your payment of Php ${amount} for ${description} has been verified.`;
      break;
    case "rejected":
      title = "Payment Rejected";
      body = `Your payment of Php ${amount} for ${description} was rejected. Please check your billing details.`;
      priority = "high";
      break;
    case "pending":
      title = "Payment Pending";
      body = `Your payment of Php ${amount} for ${description} is pending verification.`;
      break;
    default:
      title = "Payment Status Update";
      body = `Your payment of Php ${amount} for ${description} status is now: ${status}`;
  }

  return {
    title,
    body,
    subject: title,
    message: body,
    data: {
      url: `/payment-confirmed/${billingId}`,
      type: "payment_status",
      paymentId,
      status,
      amount,
      description,
      priority,
    },
    dateTime: new Date(),
    read: false,
  };
};

// Create admin notification for payment verification
export const createAdminPaymentNotification = (
  paymentId,
  amount,
  description,
  billingId,
  userName,
  userUnit
) => {
  const title = "New Payment Uploaded";
  const body = `${userName} has uploaded a payment of Php ${amount} for ${description}`;
  const subject = "New Payment Requires Verification";
  const message = `${userName} from Unit ${userUnit} has uploaded a payment of Php ${amount} for ${description}. Please verify the deposit slip.`;

  return {
    title,
    body,
    subject,
    message,
    data: {
      url: `/billing-details/${billingId}`,
      type: "payment_verification_needed",
      paymentId,
      amount,
      description,
      userUnit,
      userName,
      priority: "high",
    },
    dateTime: new Date(),
    read: false,
  };
};

// Test function to send a notification to the current user for debugging
export const sendTestNotification = async (currentUser) => {
  if (!currentUser || !currentUser.email) {
    console.error("No current user available for test notification");
    return false;
  }

  try {
    const title = "Test Notification";
    const body =
      "This is a test notification to verify that notifications are working correctly";

    const testNotification = {
      // FCM fields
      title,
      body,
      // Firestore fields
      subject: title,
      message: body,
      from: "system",
      fromName: "System Notification",
      data: {
        url: "/notifications",
        type: "test",
        timestamp: new Date().toISOString(),
      },
    };

    // Store in Firestore
    const success = await storeNotification(
      currentUser.email,
      testNotification
    );

    if (success) {
      // Try sending via FCM
      const fcmResult = await sendNotification(
        currentUser.email,
        testNotification
      );

      return true;
    } else {
      console.error("Failed to store test notification");
      return false;
    }
  } catch (error) {
    console.error("Error sending test notification:", error);
    return false;
  }
};

// Debug function to log notification permission status and token
export const debugNotificationStatus = async () => {
  try {
    const permissionStatus = Notification.permission;

    if (permissionStatus === "granted") {
      const token = await getFCMToken();
      return { permissionStatus, token };
    }

    return { permissionStatus, token: null };
  } catch (error) {
    console.error("Error checking notification status:", error);
    return { error: error.message };
  }
};
